import { useCallback, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AsyncActivity, type WS } from '@trinity/chat';
import { FileDownloadActivityItem } from '@trinity/chat-components/components/FileDownloadActivity';
import { useDownloader } from '@trinity/chat-components/hooks/useDownloader';
import { queryKeys } from '@trinity/chat-components/query-keys';
import { QueryDownloaderContext } from './Context';
import type { DownloaderItem } from './interfaces';
import { SocketSubscriber } from './SocketSubscriber';

type Props = {
  index: number;
  item: DownloaderItem;
};

export const QueryDownloaderItem = ({ index, item }: Props) => {
  const { remove, set } = useContext(QueryDownloaderContext);
  const download = useDownloader();
  const queryClient = useQueryClient();

  const { extension, id, status, websocketKey } = item;

  const onRemove = useCallback(() => {
    remove(id);
  }, [id, remove]);

  const handleComplete = useCallback((event: WS.Reports.ReportAvailable.Payload) => {
    if (event.type === AsyncActivity.FileDownload) {
      // TypeScript should properly narrow the type here
      if ('url' in event.data) {
        download({
          url: event.data.url,
        });
        set(id, {
          status: `transferring`,
        });
      }
    } else if (event.type === AsyncActivity.QuantAnalysisFileUpload) {
      // For quant analysis uploads, invalidate relevant queries and mark as complete
      if ('projectId' in event.data && 'quantAnalysisId' in event.data) {
        const { projectId, quantAnalysisId } = event.data;

        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.quantAnalysis.one({ projectId, quantAnalysisId }),
        });

        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.quantAnalysis.versions({ projectId, quantAnalysisId }),
        });

        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.quantAnalysis.list({ projectId }),
        });

        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.one({ projectId }),
        });
      }

      set(id, {
        status: `complete`,
      });
      // Remove the item after a short delay to allow users to see the completion
      setTimeout(() => {
        remove(id);
      }, 2000);
    }
  }, [download, id, set, remove, queryClient]);

  const handleError = useCallback(() => {
    set(id, {
      status: `error`,
      websocketKey: null,
    });
  }, [
    id,
    set,
  ]);

  const renderItem = useCallback(() => {
    return (
      <FileDownloadActivityItem
        extension={extension}
        filename={item.name}
        index={index}
        onCancel={onRemove}
        onRemove={onRemove}
        status={status}
        title={item.title} />
    );
  }, [
    extension,
    index,
    item.name,
    item.title,
    onRemove,
    status,
  ]);

  if (!websocketKey) {
    return renderItem();
  }

  return (
    <SocketSubscriber
      onError={handleError}
      onReady={handleComplete}
      socketKey={websocketKey}>
      {renderItem()}
    </SocketSubscriber>
  );
};

QueryDownloaderItem.displayName = 'QueryDownloader.Item';